/* eslint max-statements: 0 */

export const dispatchFetch = (action) => ({ ...action, payload: action.payload() })

/**
 * Local-storage based caching middleware.
 * Caches results of Promise-based actions in localStorage, based on unique key and duration.
 * Meant to be used for data that comes from an API, but doesn't change often, like
 * activity types.
 *
 * ------------------------------
 *
 * Starting with a brand new, empty cache, these should
 * be the order of events fired:
 *
 * 1. Nothing in cache:
 *
 * FETCH_ACTIVITY_TYPES
 *   - cache miss
 *   -> refires FETCH_ACTIVITY_TYPES as a promise action
 *   FETCH_ACTIVITY_TYPES_LOADING
 *   FETCH_ACTIVITY_TYPES_SUCCESS
 *     - middleware stashes data in local storage
 *
 * 2. Cache hit
 *
 * FETCH_ACTIVITY_TYPES
 *   - cache hit
 *   - refires FETCH_ACTIVITY_TYPES with resolved cache data
 *
 * ------------------------------
 *
 * Example use in action creator:
 *
 * function getActivityTypes () {
 *   return (dispatch, getState, { mapmy }) => {
 *     return dispatch({
 *       type: FETCH_ACTIVITY_TYPES,
 *       payload: () => mapmy.fetchActivityTypes(),
 *       meta: {
 *         localCacheKey: 'activityTypes',
 *         localCacheDuration: 86400000, // one day, in ms
 *       },
 *     })
 *   }
 * }
 *
 * -----------------------------
 *
 * @param  {String} appNonce - unique key that allows us to force clear cache
 * @return {Function}
 */
const localCacheMiddleware = (localCacheNonce) => () => (next) => (action) => {
  const { type, meta } = action
  const usingLocalCache = meta && meta.localCacheKey && meta.localCacheDuration
  const invalidType = type.match(/(_LOADING|_ERROR|_SUCCESS)$/)

  // bail early if not using localCache
  if (!usingLocalCache) { return next(action) }

  const { localCacheKey, localCacheDuration } = meta

  // stash successful promise value
  if (type.match(/_SUCCESS$/) && usingLocalCache) {
    const cacheObject = { nonce: localCacheNonce, payload: action.payload, createdAt: (new Date()) }
    localStorage.setItem(localCacheKey, JSON.stringify(cacheObject))
  }

  // bail if action isn't setup for localCache or
  // if not the original action (before promise middleware)
  if (invalidType) { return next(action) }

  let cacheObject

  try {
    const cacheString = localStorage.getItem(localCacheKey)
    cacheObject = JSON.parse(cacheString)
  } catch (e) {
    return next(dispatchFetch(action))
  }

  if (!cacheObject) { return next(dispatchFetch(action)) }

  // determine if a cache hit or miss, or override (mismatch nonce)
  const { createdAt, nonce: itemNonce, payload } = cacheObject
  const timeDelta = (new Date()) - new Date(createdAt)
  const shouldUseCache = localCacheNonce === itemNonce && timeDelta < localCacheDuration

  // cache hit: re-trigger the current action,
  // but with the resolved data from local storage
  if (shouldUseCache) {
    const newAction = { ...action, payload: Promise.resolve(payload), meta: null }
    return next(newAction)
  } else {
    // cache miss: just dispatch fetch like normal
    return next(dispatchFetch(action))
  }
}

export default localCacheMiddleware



// WEBPACK FOOTER //
// ./src/application/middleware/localCacheMiddleware.js